import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import { useAuth } from '../context/AuthContext';
import '../styles/upload.css';

const Upload = () => {
  const navigate = useNavigate();
  const { account } = useWallet();
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    price: '',
    accessType: 'public',
    tags: '',
    file: null
  });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [previewUrl, setPreviewUrl] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        file: file
      });

      // Create a preview URL for the file (if it's an image)
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrl(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setPreviewUrl(null);
      }
    }
  };

  const nextStep = () => {
    // Validate current step
    if (currentStep === 1) {
      if (!formData.file) {
        setUploadStatus({
          success: false,
          message: 'Please select a file to upload'
        });
        return;
      }
    } else if (currentStep === 2) {
      if (!formData.name || !formData.description || !formData.category) {
        setUploadStatus({
          success: false,
          message: 'Please fill in all required fields'
        });
        return;
      }
    }

    setCurrentStep(currentStep + 1);
    setUploadStatus(null);
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
    setUploadStatus(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Final validation
    if (!formData.name || !formData.description || !formData.category || !formData.price || !formData.file) {
      setUploadStatus({
        success: false,
        message: 'Please fill in all required fields'
      });
      return;
    }

    setIsUploading(true);
    setUploadStatus(null);

    // Simulate upload process
    try {
      // In a real implementation, this would:
      // 1. Upload file to IPFS
      // 2. Get back the IPFS CID
      // 3. Call smart contract to register the dataset
      // 4. Update the database with metadata

      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate network delay

      setUploadStatus({
        success: true,
        message: 'Dataset uploaded successfully! Once transaction is confirmed, it will appear in the marketplace.'
      });

      // Reset form
      setFormData({
        name: '',
        description: '',
        category: '',
        price: '',
        accessType: 'public',
        tags: '',
        file: null
      });
      setPreviewUrl(null);
      setCurrentStep(1);

      // Reset file input
      document.getElementById('file-upload').value = '';
    } catch (error) {
      setUploadStatus({
        success: false,
        message: `Upload failed: ${error.message}`
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Function to render the appropriate step
  const renderStep = () => {
    switch(currentStep) {
      case 1:
        return (
          <div className="upload-step file-upload-step">
            <h2>Select Your File</h2>
            <p className="step-description">Choose the dataset or model file you want to upload</p>

            <div className="file-upload-container">
              <div className="file-upload-area">
                <input
                  type="file"
                  id="file-upload"
                  onChange={handleFileChange}
                  className="file-input"
                  required
                />
                <label htmlFor="file-upload" className="file-label">
                  <i className="fas fa-cloud-upload-alt"></i>
                  <span>Drag & drop your file here or click to browse</span>
                  <small>Max file size: 500MB. For larger datasets, please contact support.</small>
                </label>
              </div>

              {formData.file && (
                <div className="file-preview">
                  <div className="file-info">
                    <i className="fas fa-file"></i>
                    <div className="file-details">
                      <h4>{formData.file.name}</h4>
                      <p>{(formData.file.size / (1024 * 1024)).toFixed(2)} MB</p>
                    </div>
                  </div>
                  {previewUrl && (
                    <div className="image-preview">
                      <img src={previewUrl} alt="Preview" />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="step-actions">
              <button
                type="button"
                className="btn btn-primary next-btn"
                onClick={nextStep}
                disabled={!formData.file}
              >
                Next <i className="fas fa-arrow-right"></i>
              </button>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="upload-step metadata-step">
            <h2>Dataset Information</h2>
            <p className="step-description">Provide details about your dataset or model</p>

            <div className="form-group">
              <label htmlFor="name">Name <span className="required">*</span></label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Dataset/Model Name"
                className="form-control"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description <span className="required">*</span></label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe your dataset or model in detail. Include information about data collection, format, potential use cases, etc."
                rows="4"
                className="form-control"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="category">Category <span className="required">*</span></label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="form-control"
                required
              >
                <option value="">Select a category</option>
                <option value="computer-vision">Computer Vision</option>
                <option value="nlp">Natural Language Processing</option>
                <option value="time-series">Time Series</option>
                <option value="tabular">Tabular Data</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="tags">Tags</label>
              <input
                type="text"
                id="tags"
                name="tags"
                value={formData.tags}
                onChange={handleInputChange}
                placeholder="Enter tags separated by commas (e.g., image, classification, neural-networks)"
                className="form-control"
              />
            </div>

            <div className="step-actions">
              <button
                type="button"
                className="btn btn-secondary prev-btn"
                onClick={prevStep}
              >
                <i className="fas fa-arrow-left"></i> Back
              </button>
              <button
                type="button"
                className="btn btn-primary next-btn"
                onClick={nextStep}
              >
                Next <i className="fas fa-arrow-right"></i>
              </button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="upload-step pricing-step">
            <h2>Pricing & Access</h2>
            <p className="step-description">Set pricing and access controls for your dataset or model</p>

            <div className="form-group">
              <label htmlFor="price">Price (ETH) <span className="required">*</span></label>
              <div className="price-input-container">
                <input
                  type="number"
                  id="price"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  className="form-control"
                  required
                />
                <span className="eth-symbol">ETH</span>
              </div>
              <small>Set to 0 for free datasets</small>
            </div>

            <div className="form-group">
              <label htmlFor="accessType">Access Type</label>
              <select
                id="accessType"
                name="accessType"
                value={formData.accessType}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="public">Public (Available to everyone)</option>
                <option value="private">Private (Only accessible to specific addresses)</option>
                <option value="token-gated">Token Gated (Requires specific token ownership)</option>
              </select>
            </div>

            {formData.accessType === 'token-gated' && (
              <div className="form-group">
                <label htmlFor="tokenAddress">Token Contract Address</label>
                <input
                  type="text"
                  id="tokenAddress"
                  name="tokenAddress"
                  placeholder="0x..."
                  className="form-control"
                />
              </div>
            )}

            <div className="step-actions">
              <button
                type="button"
                className="btn btn-secondary prev-btn"
                onClick={prevStep}
              >
                <i className="fas fa-arrow-left"></i> Back
              </button>
              <button
                type="submit"
                className="btn btn-primary submit-btn"
                onClick={handleSubmit}
                disabled={isUploading}
              >
                {isUploading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Uploading...
                  </>
                ) : (
                  <>Publish Dataset <i className="fas fa-check"></i></>
                )}
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Check if user is logged in - this should be handled by the ProtectedRoute
  // but we'll add an extra check here
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
    }
  }, [currentUser, navigate]);

  return (
    <div className="upload-page">
      <div className="upload-header">
        <div className="container">
          <h1>Share Your Dataset</h1>
          <p>Contribute to the community by sharing your AI datasets and models</p>
        </div>
      </div>

      <div className="container">
        <div className="upload-container">
          <div className="upload-progress">
            <div className={`progress-step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
              <div className="step-number">1</div>
              <div className="step-label">Select File</div>
            </div>
            <div className="progress-connector"></div>
            <div className={`progress-step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
              <div className="step-number">2</div>
              <div className="step-label">Dataset Info</div>
            </div>
            <div className="progress-connector"></div>
            <div className={`progress-step ${currentStep >= 3 ? 'active' : ''}`}>
              <div className="step-number">3</div>
              <div className="step-label">Pricing & Access</div>
            </div>
          </div>

          {uploadStatus && (
            <div className={`status-message ${uploadStatus.success ? 'success' : 'error'}`}>
              {uploadStatus.success ? (
                <i className="fas fa-check-circle"></i>
              ) : (
                <i className="fas fa-exclamation-circle"></i>
              )}
              <span>{uploadStatus.message}</span>
            </div>
          )}

          <form className="upload-form">
            {renderStep()}
          </form>
        </div>

        <div className="upload-info-section">
          <div className="info-card">
            <div className="info-header">
              <i className="fas fa-info-circle"></i>
              <h3>How It Works</h3>
            </div>
            <ol className="info-steps">
              <li>
                <div className="info-step-number">1</div>
                <div className="info-step-content">
                  <h4>Upload Your File</h4>
                  <p>Your dataset or model file is uploaded to decentralized storage (IPFS)</p>
                </div>
              </li>
              <li>
                <div className="info-step-number">2</div>
                <div className="info-step-content">
                  <h4>Register Metadata</h4>
                  <p>Dataset information and IPFS reference are registered on the blockchain</p>
                </div>
              </li>
              <li>
                <div className="info-step-number">3</div>
                <div className="info-step-content">
                  <h4>Marketplace Listing</h4>
                  <p>Your dataset becomes available in the marketplace for others to discover</p>
                </div>
              </li>
              <li>
                <div className="info-step-number">4</div>
                <div className="info-step-content">
                  <h4>Earn Revenue</h4>
                  <p>Receive automatic payments when others purchase access to your dataset</p>
                </div>
              </li>
            </ol>
          </div>

          <div className="info-card">
            <div className="info-header">
              <i className="fas fa-lightbulb"></i>
              <h3>Tips for Quality Datasets</h3>
            </div>
            <ul className="tips-list">
              <li>Provide detailed descriptions of your dataset</li>
              <li>Include information about data collection methodology</li>
              <li>Mention any preprocessing steps applied</li>
              <li>Add relevant tags to improve discoverability</li>
              <li>Set a fair price based on dataset size and quality</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Upload;
